import { post, put, RestApiError } from 'core/network/RestApi';
import { generatePath } from 'react-router';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import {
  IOffsiteTemplatePreviewPayload,
  IOffsiteTemplatePreviewResponse,
  IUpdateTemplatePayload,
} from 'sections/design/template-management/entities/templates';
import { TFunction } from 'i18next';
import { TemplateType } from 'models/template/Template';
import {
  TEMPLATE_EDITOR_FAKE_CC_ID,
  TEMPLATE_EDITOR_FAKE_MACHINEGUID,
} from 'sections/design/template-management/sections/template-editor/constants';
import { ILinkLookup } from 'sections/design/template-management/entities/link-config';
import { IRcsConfig } from '../context/useRcsTemplateEditorContext';
import { RcsCompilationContextForPreview } from './getCompilationContextFromSnapshotJson';

export interface RcsPreviewTemplateRequest {
  templateValue: string;
  compilationContext: RcsCompilationContextForPreview;
  siteId: string;
  language: string;
  linkConfig: ILinkLookup[];
  templateType: string;
  t: TFunction;
}

export async function rcsPreviewTemplate({
  templateValue,
  compilationContext,
  siteId,
  language,
  linkConfig,
  templateType,
  t,
}: RcsPreviewTemplateRequest): Promise<IOffsiteTemplatePreviewResponse> {
  const payload: IOffsiteTemplatePreviewPayload = {
    channel: 'rcs',
    featureType: templateType,
    bulkFeatureType: templateType === TemplateType.BulkRetargeting ? TemplateType.CartAbandonment : undefined,
    template: {
      type: 'body',
      value: templateValue,
    },
    compilationContext: compilationContext as IOffsiteTemplatePreviewPayload['compilationContext'],
    linkConfig,
    ids: {
      siteId,
      compilationId: TEMPLATE_EDITOR_FAKE_CC_ID,
      snapshotId: TEMPLATE_EDITOR_FAKE_CC_ID,
      machineGuid: TEMPLATE_EDITOR_FAKE_MACHINEGUID,
    },
    language,
    lookbackPeriod: 1440,
  };

  try {
    const result = await post<IOffsiteTemplatePreviewResponse, IOffsiteTemplatePreviewPayload>(
      generatePath(TemplateApiEndpoints.OFFSITE_PREVIEW),
      payload,
    );
    if (result?.data == null) {
      throw Error(t('designGallery.templateEditor.queries.preview.error'), {
        cause: 'Unknown error',
      } as ErrorOptions);
    }
    const data = result.data;
    if (typeof data?.compiled !== 'string') {
      throw Error(t('designGallery.templateEditor.queries.preview.error'), {
        cause: 'Invalid response',
      } as ErrorOptions);
    }
    return data;
  } catch (e) {
    const errorDetails = (e as RestApiError).response?.data?.message ?? '';
    throw Error(
      t('designGallery.templateEditor.queries.preview.error'),
      errorDetails ? ({ cause: errorDetails } as ErrorOptions) : null,
    );
  }
}

export interface SaveRcsTemplateRequest {
  siteId: string;
  templateId: string;
  templateName: string;
  templateBody: string;
  language: string;
  type: string;
  enrichmentConfigIds?: string[];
  /** Snapshot of rcsConfig used for this save; used by caller in onSuccess only, not sent to API */
  rcsConfigSnapshot?: IRcsConfig;
  t: TFunction;
}

export async function saveRcsTemplate({
  siteId,
  templateId,
  templateName,
  templateBody,
  language,
  type,
  enrichmentConfigIds,
  t,
}: SaveRcsTemplateRequest): Promise<void> {
  try {
    const payload = {
      templateName,
      template: templateBody,
      comment: 'RCS template update',
      language,
      status: 'DRAFT' as const,
      enrichmentConfigIds: enrichmentConfigIds ?? [],
      type,
    };
    const result = await put<unknown, IUpdateTemplatePayload & { type: string }>(
      generatePath(TemplateApiEndpoints.UPDATE_TEMPLATE, { siteId, templateId }),
      payload,
    );
    if (result?.data === null) {
      throw new Error(t('designGallery.templateEditor.queries.save.error'));
    }
  } catch {
    throw new Error(t('designGallery.templateEditor.queries.save.error'));
  }
}
